import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import * as reportsApi from '@/services/api/reports';
import type { ProjectReportRecord } from '@/types/project-report';
import type { ProjectWorkItemFormValue } from '@/types/report-project-work-item-lite';
import type {
  CreateReportPayload,
  ReportRecord,
  UpdateReportPayload,
} from '@/types/report';
import {
  DELETE_FUSION_FALLBACK_APPLIED_WARNING,
  DELETE_FUSION_FALLBACK_UNRELIABLE_WARNING,
  DELETE_FUSION_FALLBACK_WITH_HISTORICAL_WARNING,
} from '@/constants/phase4-r-lite';
import {
  mergeReportLiteBundleItems,
  readReportLiteBundle,
  removeReportLiteBundle,
  replaceReportLiteBundle,
  REPORT_LITE_BUNDLE_SOURCE,
  type ReportLiteBundleItem,
} from '@/utils/report-lite-bundle-store';
import { clearReportDraft } from '@/utils/report-draft';
import {
  createProjectWorkItemClientKey,
  validateProjectWorkItems,
} from '@/utils/report-project-work-item-lite';

const PROJECT_REPORTS_FETCH_PAGE_SIZE = 100;

export type ProjectWorkItemSyncOperation = 'create' | 'update' | 'delete';

export interface SubmitDailyReportPayload {
  userId: string;
  reportDate: string;
  reportBody: CreateReportPayload | UpdateReportPayload;
  projectWorkItems: ProjectWorkItemFormValue[];
  existingReportId?: string;
  isUpdate: boolean;
  /** T3.1：保存修改时用于 diff（加载时的 project_reports 快照） */
  baselineProjectWorkItems?: ProjectWorkItemFormValue[];
}

export interface ProjectWorkItemSubmitFailure {
  operation: ProjectWorkItemSyncOperation;
  item: ProjectWorkItemFormValue;
  errorMessage: string;
}

export interface SubmitDailyReportFusionResult {
  report: ReportRecord;
  projectWorkItems: ProjectWorkItemFormValue[];
  failures: ProjectWorkItemSubmitFailure[];
  partialFailure: boolean;
}

function sortedWorkItems(items: ProjectWorkItemFormValue[]): ProjectWorkItemFormValue[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}

function toCreateProjectReportPayload(
  item: ProjectWorkItemFormValue,
  reportDate: string,
): Parameters<typeof projectReportsApi.createProjectReport>[0] {
  return {
    projectId: item.projectId,
    reportPeriod: 'DAILY',
    reportDate,
    progressPercent: item.progressPercent,
    progressContent: item.progressContent.trim(),
    problemContent: item.problemContent?.trim() || undefined,
    nextPlanContent: item.nextPlanContent?.trim() || undefined,
    status: 'SUBMITTED',
  };
}

function toUpdateProjectReportPayload(
  item: ProjectWorkItemFormValue,
): Parameters<typeof projectReportsApi.updateProjectReport>[1] {
  return {
    progressPercent: item.progressPercent,
    progressContent: item.progressContent.trim(),
    problemContent: item.problemContent?.trim() || null,
    nextPlanContent: item.nextPlanContent?.trim() || null,
  };
}

async function createProjectReportForItem(
  item: ProjectWorkItemFormValue,
  reportDate: string,
): Promise<ProjectWorkItemFormValue> {
  const created = await projectReportsApi.createProjectReport(
    toCreateProjectReportPayload(item, reportDate),
  );
  return {
    ...item,
    projectReportId: created.id,
  };
}

async function updateProjectReportForItem(
  item: ProjectWorkItemFormValue,
): Promise<ProjectWorkItemFormValue> {
  if (!item.projectReportId) {
    throw new Error('Missing projectReportId');
  }
  await projectReportsApi.updateProjectReport(item.projectReportId, toUpdateProjectReportPayload(item));
  return item;
}

function persistLiteBundleAfterCreate(
  userId: string,
  reportId: string,
  reportDate: string,
  items: ProjectWorkItemFormValue[],
  newBundleItems: ReportLiteBundleItem[],
): void {
  if (newBundleItems.length > 0) {
    mergeReportLiteBundleItems(userId, reportId, reportDate, newBundleItems);
  }
  replaceReportLiteBundle(userId, reportId, reportDate, items);
}

/** 首次提交：仅 POST 新建块 */
async function submitProjectWorkItemsOnCreate(
  userId: string,
  reportId: string,
  reportDate: string,
  allItems: ProjectWorkItemFormValue[],
): Promise<{
  mergedItems: ProjectWorkItemFormValue[];
  failures: ProjectWorkItemSubmitFailure[];
}> {
  const mergedByClientKey = new Map(allItems.map((item) => [item.clientKey, { ...item }]));
  const failures: ProjectWorkItemSubmitFailure[] = [];
  const succeededBundleItems: ReportLiteBundleItem[] = [];

  for (const item of sortedWorkItems(allItems)) {
    if (item.projectReportId) {
      continue;
    }
    try {
      const saved = await createProjectReportForItem(item, reportDate);
      mergedByClientKey.set(item.clientKey, saved);
      succeededBundleItems.push({
        projectReportId: saved.projectReportId!,
        projectId: saved.projectId,
        sortOrder: saved.sortOrder,
      });
    } catch (error) {
      failures.push({
        operation: 'create',
        item,
        errorMessage: error instanceof ApiError ? error.message : '保存失败',
      });
    }
  }

  const mergedItems = sortedWorkItems([...mergedByClientKey.values()]);
  persistLiteBundleAfterCreate(userId, reportId, reportDate, mergedItems, succeededBundleItems);

  return { mergedItems, failures };
}

/** T3.1：保存修改 — DELETE → PATCH → POST */
async function syncProjectWorkItemsOnUpdate(
  userId: string,
  reportId: string,
  reportDate: string,
  currentItems: ProjectWorkItemFormValue[],
  baselineProjectWorkItems: ProjectWorkItemFormValue[],
): Promise<{
  mergedItems: ProjectWorkItemFormValue[];
  failures: ProjectWorkItemSubmitFailure[];
}> {
  const failures: ProjectWorkItemSubmitFailure[] = [];
  const mergedByClientKey = new Map(currentItems.map((item) => [item.clientKey, { ...item }]));

  const baselineProjectReportIds = extractProjectReportIds(baselineProjectWorkItems);
  const baselineByReportId = new Map(
    baselineProjectWorkItems
      .filter((item) => item.projectReportId)
      .map((item) => [item.projectReportId!, item]),
  );

  const currentIds = new Set(
    currentItems.filter((item) => item.projectReportId).map((item) => item.projectReportId!),
  );
  const toDelete = baselineProjectReportIds.filter((id) => !currentIds.has(id));
  const toUpdate = sortedWorkItems(currentItems.filter((item) => item.projectReportId));
  const toCreate = sortedWorkItems(currentItems.filter((item) => !item.projectReportId));

  for (const projectReportId of toDelete) {
    const baselineItem = baselineByReportId.get(projectReportId);
    const ghostItem: ProjectWorkItemFormValue = baselineItem ?? {
      clientKey: createProjectWorkItemClientKey(),
      projectReportId,
      projectId: '',
      projectName: '已移除项目',
      sortOrder: 0,
      progressPercent: 0,
      progressContent: '',
    };
    try {
      await projectReportsApi.deleteProjectReport(projectReportId);
    } catch (error) {
      failures.push({
        operation: 'delete',
        item: ghostItem,
        errorMessage: error instanceof ApiError ? error.message : '删除失败',
      });
    }
  }

  for (const item of toUpdate) {
    try {
      const saved = await updateProjectReportForItem(item);
      mergedByClientKey.set(item.clientKey, saved);
    } catch (error) {
      failures.push({
        operation: 'update',
        item,
        errorMessage: error instanceof ApiError ? error.message : '更新失败',
      });
    }
  }

  for (const item of toCreate) {
    try {
      const saved = await createProjectReportForItem(item, reportDate);
      mergedByClientKey.set(item.clientKey, saved);
    } catch (error) {
      failures.push({
        operation: 'create',
        item,
        errorMessage: error instanceof ApiError ? error.message : '保存失败',
      });
    }
  }

  const mergedItems = sortedWorkItems([...mergedByClientKey.values()]);
  replaceReportLiteBundle(userId, reportId, reportDate, mergedItems);

  return { mergedItems, failures };
}

export async function submitDailyReportWithProjectItems(
  payload: SubmitDailyReportPayload,
): Promise<SubmitDailyReportFusionResult> {
  const {
    userId,
    reportDate,
    reportBody,
    projectWorkItems,
    existingReportId,
    isUpdate,
    baselineProjectWorkItems = [],
  } = payload;

  if (projectWorkItems.length > 0) {
    const validationError = validateProjectWorkItems(projectWorkItems);
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const report = isUpdate && existingReportId
    ? await reportsApi.updateReport(existingReportId, reportBody as UpdateReportPayload)
    : await reportsApi.createReport(reportBody as CreateReportPayload);

  if (isUpdate) {
    const { mergedItems, failures } = await syncProjectWorkItemsOnUpdate(
      userId,
      report.id,
      reportDate,
      projectWorkItems,
      baselineProjectWorkItems,
    );
    return {
      report,
      projectWorkItems: mergedItems,
      failures,
      partialFailure: failures.length > 0,
    };
  }

  if (projectWorkItems.length === 0) {
    return {
      report,
      projectWorkItems: [],
      failures: [],
      partialFailure: false,
    };
  }

  const { mergedItems, failures } = await submitProjectWorkItemsOnCreate(
    userId,
    report.id,
    reportDate,
    projectWorkItems,
  );

  return {
    report,
    projectWorkItems: mergedItems,
    failures,
    partialFailure: failures.length > 0,
  };
}

/** 重试失败项（按 operation 重试） */
export async function retryFailedProjectWorkItems(params: {
  userId: string;
  reportId: string;
  reportDate: string;
  allItems: ProjectWorkItemFormValue[];
  failures: ProjectWorkItemSubmitFailure[];
}): Promise<SubmitDailyReportFusionResult> {
  const { userId, reportId, reportDate, allItems, failures } = params;

  const report = await reportsApi.fetchReport(reportId);
  const retryFailures: ProjectWorkItemSubmitFailure[] = [];
  let workingItems = sortedWorkItems(allItems.map((item) => ({ ...item })));

  for (const failure of failures) {
    try {
      if (failure.operation === 'delete' && failure.item.projectReportId) {
        await projectReportsApi.deleteProjectReport(failure.item.projectReportId);
      } else if (failure.operation === 'update') {
        await updateProjectReportForItem(failure.item);
      } else if (failure.operation === 'create') {
        const saved = await createProjectReportForItem(failure.item, reportDate);
        workingItems = workingItems.map((item) =>
          item.clientKey === failure.item.clientKey ? saved : item,
        );
      }
    } catch (error) {
      retryFailures.push({
        operation: failure.operation,
        item: failure.item,
        errorMessage: error instanceof ApiError ? error.message : '重试失败',
      });
    }
  }

  replaceReportLiteBundle(userId, reportId, reportDate, workingItems);

  return {
    report,
    projectWorkItems: workingItems,
    failures: retryFailures,
    partialFailure: retryFailures.length > 0,
  };
}

export function normalizeReportDateKey(reportDate: string): string {
  return reportDate.slice(0, 10);
}

export function isProjectReportOnDate(record: ProjectReportRecord, dateKey: string, userId: string): boolean {
  return (
    record.reportPeriod === 'DAILY' &&
    normalizeReportDateKey(record.reportDate) === dateKey &&
    record.createdBy.id === userId
  );
}

export function mapProjectReportsToWorkItems(
  records: ProjectReportRecord[],
  dateKey: string,
  userId: string,
  bundleSortOrder?: Map<string, number>,
): ProjectWorkItemFormValue[] {
  const todayRecords = records.filter((record) => isProjectReportOnDate(record, dateKey, userId));

  const latestByProject = new Map<string, ProjectReportRecord>();
  for (const record of todayRecords) {
    const existing = latestByProject.get(record.project.id);
    if (!existing || record.createdAt > existing.createdAt) {
      latestByProject.set(record.project.id, record);
    }
  }

  const mapped = [...latestByProject.values()].map((record, index) => {
    const sortOrder = bundleSortOrder?.get(record.project.id) ?? index;
    return {
      clientKey: createProjectWorkItemClientKey(),
      projectReportId: record.id,
      projectId: record.project.id,
      projectName: record.project.name,
      sortOrder,
      progressPercent: record.progressPercent,
      progressContent: record.progressContent,
      problemContent: record.problemContent ?? '',
      nextPlanContent: record.nextPlanContent ?? '',
    } satisfies ProjectWorkItemFormValue;
  });

  return mapped.sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function fetchProjectReportsForUserOnDate(
  submitterUserId: string,
  dateKey: string,
): Promise<ProjectReportRecord[]> {
  const result = await projectReportsApi.fetchProjectReports({
    page: 1,
    pageSize: PROJECT_REPORTS_FETCH_PAGE_SIZE,
    reportPeriod: 'DAILY',
  });
  return result.items.filter((record) => isProjectReportOnDate(record, dateKey, submitterUserId));
}

/** @deprecated 别名，语义同 fetchProjectReportsForUserOnDate */
export async function fetchTodayProjectReportsForUser(
  userId: string,
  dateKey: string,
): Promise<ProjectReportRecord[]> {
  return fetchProjectReportsForUserOnDate(userId, dateKey);
}

/** T4：详情页展示 — sortOrder 优先，无 Bundle 时 createdAt 升序 */
export function mapProjectReportsToDisplayItems(
  records: ProjectReportRecord[],
  dateKey: string,
  submitterUserId: string,
  bundleSortOrder?: Map<string, number>,
): ProjectWorkItemFormValue[] {
  const dayRecords = records.filter((record) =>
    isProjectReportOnDate(record, dateKey, submitterUserId),
  );

  const latestByProject = new Map<string, ProjectReportRecord>();
  for (const record of dayRecords) {
    const existing = latestByProject.get(record.project.id);
    if (!existing || record.createdAt > existing.createdAt) {
      latestByProject.set(record.project.id, record);
    }
  }

  let orderedRecords: ProjectReportRecord[];
  if (bundleSortOrder && bundleSortOrder.size > 0) {
    orderedRecords = [...latestByProject.values()].sort((a, b) => {
      const orderA = bundleSortOrder.get(a.project.id) ?? Number.MAX_SAFE_INTEGER;
      const orderB = bundleSortOrder.get(b.project.id) ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.createdAt.localeCompare(b.createdAt);
    });
  } else {
    orderedRecords = [...latestByProject.values()].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
  }

  return orderedRecords.map((record, index) => ({
    clientKey: record.id,
    projectReportId: record.id,
    projectId: record.project.id,
    projectName: record.project.name,
    sortOrder: bundleSortOrder?.get(record.project.id) ?? index,
    progressPercent: record.progressPercent,
    progressContent: record.progressContent,
    problemContent: record.problemContent ?? '',
    nextPlanContent: record.nextPlanContent ?? '',
  }));
}

export async function loadReportProjectWorkItemsForDisplay(
  submitterUserId: string,
  reportId: string,
  reportDate: string,
): Promise<ProjectWorkItemFormValue[]> {
  const dateKey = normalizeReportDateKey(reportDate);
  const records = await fetchProjectReportsForUserOnDate(submitterUserId, dateKey);
  const bundle = readReportLiteBundle(submitterUserId, reportId);
  const sortMap = bundle ? buildBundleSortOrderMap(bundle.items) : undefined;
  return mapProjectReportsToDisplayItems(records, dateKey, submitterUserId, sortMap);
}

export function buildBundleSortOrderMap(
  items: Array<{ projectId: string; sortOrder: number }>,
): Map<string, number> {
  return new Map(items.map((item) => [item.projectId, item.sortOrder]));
}

export function extractProjectReportIds(items: ProjectWorkItemFormValue[]): string[] {
  return items.filter((item) => item.projectReportId).map((item) => item.projectReportId!);
}

export function formatProjectWorkItemFailureLabel(failure: ProjectWorkItemSubmitFailure): string {
  const name = failure.item.projectName || '未知项目';
  if (failure.operation === 'delete') {
    return `${name}（删除）`;
  }
  if (failure.operation === 'update') {
    return `${name}（更新）`;
  }
  return name;
}

export interface ProjectReportDeleteFailure {
  projectReportId: string;
  projectName: string;
  errorMessage: string;
}

export interface DeleteDailyReportFusionResult {
  success: boolean;
  projectReportFailures: ProjectReportDeleteFailure[];
  reportDeleteFailed: boolean;
  reportDeleteErrorMessage?: string;
  /** T6.1-B：无 Bundle 时使用 fallback 的提示 */
  fusionFallbackWarning?: string;
}

export interface DeleteDailyReportFusionContext {
  reportSubmittedAt?: string | null;
  reportCreatedAt?: string;
}

interface FusionDeleteTarget {
  projectReportId: string;
  projectId: string;
  projectName: string;
}

function resolveProjectNameForTarget(
  target: Pick<FusionDeleteTarget, 'projectReportId' | 'projectId' | 'projectName'>,
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> },
): string {
  return (
    nameLookup.byReportId.get(target.projectReportId) ??
    nameLookup.byProjectId.get(target.projectId) ??
    target.projectName ??
    '未知项目'
  );
}

function resolveFusionDeleteTargetsFromBundle(
  submitterUserId: string,
  reportId: string,
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> },
): FusionDeleteTarget[] | null {
  const bundle = readReportLiteBundle(submitterUserId, reportId);
  if (!bundle || bundle.source !== REPORT_LITE_BUNDLE_SOURCE || bundle.items.length === 0) {
    return null;
  }

  return bundle.items.map((item) => ({
    projectReportId: item.projectReportId,
    projectId: item.projectId,
    projectName: resolveProjectNameForTarget(
      { projectReportId: item.projectReportId, projectId: item.projectId, projectName: '' },
      nameLookup,
    ),
  }));
}

/** T6.1-B：无 Lite Store 时，按提交时间推断 fusion 创建的项目日报 */
async function resolveFusionDeleteTargetsFallback(
  submitterUserId: string,
  reportDate: string,
  context: DeleteDailyReportFusionContext,
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> },
): Promise<{
  targets: FusionDeleteTarget[];
  warning?: string;
  skipFusionDeletes: boolean;
}> {
  const anchorTime = context.reportSubmittedAt ?? context.reportCreatedAt;
  if (!anchorTime) {
    return {
      targets: [],
      skipFusionDeletes: true,
      warning: DELETE_FUSION_FALLBACK_UNRELIABLE_WARNING,
    };
  }

  const dateKey = normalizeReportDateKey(reportDate);
  const records = await fetchProjectReportsForUserOnDate(submitterUserId, dateKey);
  const afterAnchor = records.filter((record) => record.createdAt >= anchorTime);

  const latestByProject = new Map<string, ProjectReportRecord>();
  for (const record of afterAnchor) {
    const existing = latestByProject.get(record.project.id);
    if (!existing || record.createdAt > existing.createdAt) {
      latestByProject.set(record.project.id, record);
    }
  }

  const targets = [...latestByProject.values()].map((record) => ({
    projectReportId: record.id,
    projectId: record.project.id,
    projectName: resolveProjectNameForTarget(
      {
        projectReportId: record.id,
        projectId: record.project.id,
        projectName: record.project.name,
      },
      nameLookup,
    ),
  }));

  const hasHistoricalSameDay = records.some((record) => record.createdAt < anchorTime);
  let warning: string | undefined;
  if (targets.length > 0) {
    warning = hasHistoricalSameDay
      ? DELETE_FUSION_FALLBACK_WITH_HISTORICAL_WARNING
      : DELETE_FUSION_FALLBACK_APPLIED_WARNING;
  }

  return { targets, warning, skipFusionDeletes: false };
}

async function resolveFusionDeleteTargets(
  submitterUserId: string,
  reportId: string,
  reportDate: string,
  context: DeleteDailyReportFusionContext,
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> },
): Promise<{
  targets: FusionDeleteTarget[];
  fusionFallbackWarning?: string;
}> {
  const fromBundle = resolveFusionDeleteTargetsFromBundle(submitterUserId, reportId, nameLookup);
  if (fromBundle) {
    return { targets: fromBundle };
  }

  const fallback = await resolveFusionDeleteTargetsFallback(
    submitterUserId,
    reportDate,
    context,
    nameLookup,
  );

  if (fallback.skipFusionDeletes) {
    return { targets: [], fusionFallbackWarning: fallback.warning };
  }

  return {
    targets: fallback.targets,
    fusionFallbackWarning: fallback.warning,
  };
}

async function deleteFusionProjectReports(
  targets: FusionDeleteTarget[],
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> },
): Promise<ProjectReportDeleteFailure[]> {
  const failures: ProjectReportDeleteFailure[] = [];

  for (const target of targets) {
    const projectName = resolveProjectNameForTarget(target, nameLookup);
    try {
      await projectReportsApi.deleteProjectReportOrGone(target.projectReportId);
    } catch (error) {
      failures.push({
        projectReportId: target.projectReportId,
        projectName,
        errorMessage: error instanceof ApiError ? error.message : '删除失败',
      });
    }
  }

  return failures;
}

export function buildProjectNameLookup(
  workItems: ProjectWorkItemFormValue[],
  projects: Array<{ id: string; name: string }> = [],
): { byReportId: Map<string, string>; byProjectId: Map<string, string> } {
  const byReportId = new Map<string, string>();
  const byProjectId = new Map<string, string>();

  for (const item of workItems) {
    if (item.projectReportId) {
      byReportId.set(item.projectReportId, item.projectName || '未知项目');
    }
    if (item.projectId) {
      byProjectId.set(item.projectId, item.projectName || '未知项目');
    }
  }

  for (const project of projects) {
    byProjectId.set(project.id, project.name);
  }

  return { byReportId, byProjectId };
}

export function formatProjectReportDeleteFailureMessage(
  failures: ProjectReportDeleteFailure[],
): string {
  const lines = failures.map((failure) => `· ${failure.projectName}`).join('\n');
  return `以下项目进展删除失败：\n\n${lines}\n\n请重试。`;
}

/** T6 / L3：先删 fusion 项目日报，再删日报，最后清 Bundle */
export async function deleteDailyReportWithFusion(
  submitterUserId: string,
  reportId: string,
  reportDate: string,
  nameLookup: { byReportId: Map<string, string>; byProjectId: Map<string, string> } = {
    byReportId: new Map(),
    byProjectId: new Map(),
  },
  context: DeleteDailyReportFusionContext = {},
): Promise<DeleteDailyReportFusionResult> {
  const { targets, fusionFallbackWarning } = await resolveFusionDeleteTargets(
    submitterUserId,
    reportId,
    reportDate,
    context,
    nameLookup,
  );

  const projectReportFailures = await deleteFusionProjectReports(targets, nameLookup);

  if (projectReportFailures.length > 0) {
    return {
      success: false,
      projectReportFailures,
      reportDeleteFailed: false,
      fusionFallbackWarning,
    };
  }

  try {
    await reportsApi.deleteReport(reportId);
  } catch (error) {
    return {
      success: false,
      projectReportFailures: [],
      reportDeleteFailed: true,
      reportDeleteErrorMessage: error instanceof ApiError ? error.message : '日报删除失败',
      fusionFallbackWarning,
    };
  }

  removeReportLiteBundle(submitterUserId, reportId);
  clearReportDraft(submitterUserId, normalizeReportDateKey(reportDate));

  return {
    success: true,
    projectReportFailures: [],
    reportDeleteFailed: false,
    fusionFallbackWarning,
  };
}
